import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PilotBadge from "@/components/PilotBadge";
import AccessibilitySettingsPopup from "@/components/AccessibilitySettingsPopup";
import { useToast } from "@/hooks/use-toast";
import useAppLinks from "@/lib/hooks/useAppLinks";
import useUser from "@/lib/queries/user/useUser";
import { cn } from "@/lib/utils";
import { GraduationCap, SignIn, UserCircle } from "@phosphor-icons/react";
import { useStytch, useStytchSession } from "@stytch/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ROOT, AUTHENTICATE, SETTINGS } from "@/lib/links";
import { useTranslation } from "react-i18next";

function DesktopNav() {
  const { t } = useTranslation(["common", "settings"]);
  const stytchSession = useStytchSession();
  const { data: user } = useUser({
    enabled: stytchSession.session !== null,
  });
  const stytch = useStytch();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const logout = () => {
    stytch.session.revoke().then(() => {
      toast.toast({
        title: "Logged out",
        description: "You have been logged out.",
      });
      queryClient.removeQueries({
        queryKey: ["user"],
      });
      queryClient.invalidateQueries({
        queryKey: ["init"],
      });
      queryClient.refetchQueries({
        queryKey: ["init"],
      });

      navigate(ROOT);
      window.sessionStorage.clear();
      window.localStorage.removeItem("tchr_client_key");
    });
  };

  const { tools: appLinks, isPilot: userIsPilot } = useAppLinks();

  return (
    <nav
      className="p-4 hidden md:flex bg-card flex-col fixed left-0 top-0 h-dvh w-[70px] border-r border-border z-40"
      aria-label="Desktop Navigation"
    >
      <TooltipProvider delayDuration={100}>
        <ul
          className="flex flex-col h-full gap-[0.625rem] items-center"
          aria-label="Main Navigation"
        >
          <li className="mb-16">
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/"
                  className="inline-block"
                  tabIndex={-1}
                  aria-label="Navigate to Home"
                >
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-current="page"
                    className={cn("text-foreground", "p-[0.37rem] h-fit w-fit")}
                  >
                    <GraduationCap className="!w-10 !h-10" />
                  </Button>
                </NavLink>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                className="bg-popover-dark text-popover-dark-foreground border-popover-dark"
              >
                <span className="select-none">{t("home")}</span>
              </TooltipContent>
            </Tooltip>
          </li>
          {appLinks
            .filter((l) => !l.comingSoon)
            .map((link) => {
              const showPilotBadge = userIsPilot && link.isPilot;

              return (
                <li key={link.href} className={cn("relative", link.className)}>
                  {showPilotBadge && (
                    <PilotBadge
                      variant="icon"
                      className="absolute z-20 -top-1 -right-1"
                    />
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={link.href}
                        className={cn("inline-block")}
                        onClick={(e) =>
                          !link.active ? e.preventDefault() : null
                        }
                        tabIndex={-1}
                        aria-label={"Navigate to " + link.title}
                      >
                        {/* 
                        FIXME: There's a bug with the impl of isActive in Navlink in v6 that it's guaranteed
                         to return as a string. 
                        so for now we're nesting this even though it's ugly 
                      */}
                        {({ isActive }) => (
                          <Button
                            size="icon"
                            role="link"
                            variant="ghost"
                            disabled={!link.active}
                            aria-current={isActive ? "page" : undefined}
                            className={cn(
                              isActive
                                ? "bg-secondary dark:text-primary text-green-950 border hover:bg-secondary/70 border-menu-border"
                                : "text-foreground hover:text-green-950 dark:hover:text-primary",
                              "p-[0.37rem] h-fit w-fit border border-transparent flex flex-col"
                            )}
                          >
                            {link.icon ? (
                              <link.icon
                                className="!w-8 !h-8"
                                aria-label={link.title}
                              />
                            ) : (
                              link.title
                            )}
                          </Button>
                        )}
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      className="bg-popover-dark text-popover-dark-foreground border-popover-dark"
                    >
                      <span className="select-none">{link.title}</span>
                    </TooltipContent>
                  </Tooltip>
                </li>
              );
            })}
          <li className="mt-auto mb-4">
            <AccessibilitySettingsPopup />
          </li>
          {stytchSession.session === null ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to={AUTHENTICATE} aria-label="Sign In/Register">
                  <SignIn className="!w-8 !h-8" />
                </Link>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                className=" bg-popover-dark text-popover-dark-foreground border-popover-dark"
              >
                <span className="select-none">{t("signIn")}</span>
              </TooltipContent>
            </Tooltip>
          ) : (
            <li key="user" className="" aria-label="User Dropdown Menu">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Avatar aria-label="User Avatar" aria-haspopup="true">
                    {user?.settings?.firstName && user?.settings?.lastName ? (
                      <AvatarFallback>
                        {user?.settings?.firstName[0].toUpperCase() +
                          user?.settings?.lastName[0].toUpperCase()}
                      </AvatarFallback>
                    ) : (
                      <UserCircle className="w-full h-full" />
                    )}
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>
                    {user?.settings?.firstName
                      ? `${user?.settings?.firstName} ${user?.settings?.lastName}`
                      : "Welcome"}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <NavLink className="cursor-pointer" to={SETTINGS}>
                      {t("settings")}
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Button
                      variant="ghost"
                      onClick={logout}
                      className="justify-start w-full"
                    >
                      {t("logout")}
                    </Button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          )}
        </ul>
      </TooltipProvider>
    </nav>
  );
}

export default DesktopNav;
